/**
 * Utility functions for extracting city data from post objects.
 * Handles various post data formats and provides normalized city objects.
 */

import { CityFormData } from '@/types/city';
import Post from '@/models/post';
import { debug } from '@/utils/logger';
import type { PostWithCityData, PostLocation } from './cityExtractionTypes';
import {
  extractCityFromItineraryDestination,
  extractCityFromPostCities,
  extractCityFromTopLevelDestination,
  extractCityFromIdentifyingName,
  extractCityFromPostLocations,
} from './cityExtractionPriorityFunctions';

/**
 * Extracts city data from a post object.
 * Tries multiple sources in order of preference:
 * 1. post.cities[0] (Lists from mobile app - most complete data)
 * 2. itinerary_data.user_defined_destinations[0] (Itineraries from mobile app)
 * 3. Top-level user_defined_destinations[0] (web app Lists - legacy)
 * 4. city_identifying_names[0] (fallback - backend may populate)
 * 
 * @param post - Post object (can be any post type)
 * @returns CityFormData object or null if no city data found
 */
export function extractCityFromPost(post: Post | null | undefined): CityFormData | null {
  if (!post) {
    debug('[extractCityFromPost] No post provided');
    return null;
  }

  // Priority 0: primary_destination — user-chosen destination (city, locality, or country)
  // When present, it is the source of truth for display.
  const primaryDest = (post as unknown as PostWithCityData).primary_destination;
  if (primaryDest?.identifying_name) {
    debug('[extractCityFromPost] Using primary_destination', {
      type: primaryDest.type,
      identifying_name: primaryDest.identifying_name,
    });
    return {
      city: primaryDest.identifying_name,
      name: primaryDest.identifying_name,
      city_identifying_name: primaryDest.identifying_name,
      a3_intl_address: primaryDest.identifying_name,
      google_place_id: primaryDest.google_place_id,
      geoposition: primaryDest.geoposition,
    };
  }

  const postWithCityData = post as unknown as PostWithCityData;
  const itineraryData = postWithCityData.itinerary_data;
  const topLevelUserDefinedDestinations = postWithCityData.user_defined_destinations;
  
  const isItinerary = post.post_type === 'Itinerary' || (post.post_type as string) === 'itinerary';
  
  debug('[extractCityFromPost] Extracting city from post', {
    postId: post._id,
    postType: post.post_type,
    isItinerary,
    hasCities: !!(post.cities && Array.isArray(post.cities) && post.cities.length > 0),
    citiesCount: post.cities?.length || 0,
    hasNestedUserDefinedDestinations: !!(itineraryData?.user_defined_destinations && Array.isArray(itineraryData.user_defined_destinations) && itineraryData.user_defined_destinations.length > 0),
    hasTopLevelUserDefinedDestinations: !!(topLevelUserDefinedDestinations && Array.isArray(topLevelUserDefinedDestinations) && topLevelUserDefinedDestinations.length > 0),
    hasCityIdentifyingNames: !!(post.city_identifying_names && Array.isArray(post.city_identifying_names) && post.city_identifying_names.length > 0),
    cityIdentifyingNames: post.city_identifying_names,
  });

  // For Itineraries, check user_defined_destinations FIRST (Priority 2)
  // because post.cities[0] may have incomplete data (missing state in long_name)
  // For Lists, check post.cities[0] first (Priority 1) as it has complete data
  
  // Priority 2 (for Itineraries): itinerary_data.user_defined_destinations[0] - CHECK FIRST for Itineraries
  if (isItinerary && itineraryData?.user_defined_destinations && 
      Array.isArray(itineraryData.user_defined_destinations) && 
      itineraryData.user_defined_destinations.length > 0) {
    const destination = itineraryData.user_defined_destinations[0];
    const result = extractCityFromItineraryDestination(destination);
    if (result) {
      debug('[extractCityFromPost] Found city in itinerary_data.user_defined_destinations[0] (Priority 2 for Itineraries)');
      return result;
    }
  }

  // Priority 1: post.cities[0] (Lists from mobile app - most complete data)
  // Mobile app ALWAYS populates this for Lists
  // NOTE: For Itineraries, post.cities may have incomplete data (missing state in long_name)
  // So we check Priority 2 (user_defined_destinations) first for Itineraries if it exists
  // Only use post.cities[0] for Lists OR if Itinerary doesn't have user_defined_destinations
  if (post.cities && Array.isArray(post.cities) && post.cities.length > 0) {
    // Skip post.cities[0] for Itineraries if user_defined_destinations exists (Priority 2 has better data)
    if (!isItinerary || !itineraryData?.user_defined_destinations || 
        !Array.isArray(itineraryData.user_defined_destinations) || 
        itineraryData.user_defined_destinations.length === 0) {
      const result = extractCityFromPostCities(post.cities);
      if (result) {
        return result;
      }
    }
  }

  // Priority 3: Top-level user_defined_destinations[0] (web app Lists - legacy)
  // Web frontend may have created Lists with this structure
  if (topLevelUserDefinedDestinations && Array.isArray(topLevelUserDefinedDestinations) && topLevelUserDefinedDestinations.length > 0) {
    const destination = topLevelUserDefinedDestinations[0];
    const result = extractCityFromTopLevelDestination(destination, post.city_identifying_names);
    if (result) {
      return result;
    }
  }

  // Priority 4: city_identifying_names[0] (fallback - backend may populate)
  // Backend extracts this from other sources, but may be incomplete
  if (post.city_identifying_names && Array.isArray(post.city_identifying_names) && post.city_identifying_names.length > 0) {
    const identifyingName = post.city_identifying_names[0];
    const result = extractCityFromIdentifyingName(identifyingName);
    if (result) {
      return result;
    }
  }

  // Priority 5: Extract from postLocations when backend didn't populate destination fields
  // (common for itinerary imports saved without primary_destination).
  const rawPostLocations = post.post_locations || post.postLocations;
  const postLocations = Array.isArray(rawPostLocations)
    ? (rawPostLocations as unknown as PostLocation[])
    : undefined;
  if (postLocations && postLocations.length > 0) {
    const result = extractCityFromPostLocations(postLocations);
    if (result) {
      debug('[extractCityFromPost] Found city in postLocations (Priority 5)');
      return result;
    }
  }

  debug('[extractCityFromPost] No city data found in post');
  return null;
}

/**
 * Extracts city display name from a post object.
 * Returns a user-friendly city name for display purposes.
 * 
 * @param post - Post object
 * @returns City display name or null
 */
export function getCityDisplayNameFromPost(post: Post | null | undefined): string | null {
  const city = extractCityFromPost(post);
  if (!city) return null;

  return city.city || city.name || city.city_identifying_name || null;
}
